import { useCallback } from 'react';
import { type Edge, type Node, type ReactFlowInstance } from '@xyflow/react';
import * as Api from '@ViewModels';
import { toast } from 'sonner';
import type { BaseNodeData, FlowContact } from '../types';
import { NODE_TYPES } from '../types';
import type { SegmentNodeData } from '../types/stepNodes';
import { EDGE_TYPES } from '../types/edges';
import type { ChainTerminus } from './useChainTermini';
import type { HistoryActionType } from './useUndoRedo';
import { addSwitchStepWithSegments } from './useCanvasDragDrop';
import { insertNodeAfter } from '../utils/autoLayout';
import { createStepNodeByType } from '../utils/nodeFactory';
import { reconcileOptimisticStepNode } from '../utils/reconcileOptimisticStepNode';
import { scheduleCenterOnNode } from '../utils/viewport';

interface UseChainStepAdditionParams {
	nodes: Node<BaseNodeData>[];
	edges: Edge[];
	setNodes: React.Dispatch<React.SetStateAction<Node<BaseNodeData>[]>>;
	setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
	animateEdges: boolean;
	activeContacts: FlowContact[];
	setCenter: ReactFlowInstance['setCenter'];
	takeSnapshot: (actionType: HistoryActionType, description: string) => void;
	onAddStepToChain?: (
		terminus: ChainTerminus | null,
		stepType: string
	) => Promise<Api.IAutomationStep | null | undefined>;
	/** The automation template - needed for creating new step nodes with proper context */
	automationTemplate?: Api.IAutomationTemplate;
}

type HandleAddStepToChain = (terminus: ChainTerminus, stepType: string) => Promise<void>;

/**
 * Hook that handles adding steps to chain termini (floating + buttons).
 * Extracted from AutomationFlowCanvas to improve maintainability.
 */
export function useChainStepAddition({
	nodes,
	edges,
	setNodes,
	setEdges,
	animateEdges,
	activeContacts,
	setCenter,
	takeSnapshot,
	onAddStepToChain,
	automationTemplate,
}: UseChainStepAdditionParams): HandleAddStepToChain {
	const handleAddStepToChain = useCallback(
		async (terminus: ChainTerminus, stepType: string) => {
			try {
				// Handle switch step specially - creates segments
				if (stepType === NODE_TYPES.SWITCH_STEP) {
					// Only one conditional/switch node allowed per automation flow
					if (nodes.some(node => node.type === NODE_TYPES.SWITCH_STEP)) {
						return;
					}

					takeSnapshot('node_add', `Add ${stepType} step`);

					const parentNode = nodes.find(node => node.id === terminus.nodeId);
					const basePosition = parentNode?.position ?? { x: 0, y: 0 };

					const switchResult = addSwitchStepWithSegments(nodes, edges, basePosition, terminus.nodeId);

					const switchEdges = switchResult.edges.map(edge => ({
						...edge,
						animated: animateEdges,
						data: { ...edge.data, activeContacts },
					}));

					setNodes(switchResult.nodes);
					setEdges(switchEdges);

					const focusNode = switchResult.nodes.find(node => node.id === switchResult.focusNodeId);
					if (focusNode) {
						scheduleCenterOnNode(setCenter, focusNode, { duration: 300, zoom: 1 });
					}

					if (onAddStepToChain) {
						const createdStep = await onAddStepToChain(terminus, stepType);
						if (createdStep?.id) {
							// Update the switch node with the real stepId and automationTemplate
							setNodes(currentNodes =>
								currentNodes.map(node => {
									if (node.id !== switchResult.switchNodeId) {
										return node;
									}
									return {
										...node,
										data: {
											...node.data,
											stepId: createdStep.id,
											automationStep: createdStep,
											automationTemplate,
											stepData: {
												...(node.data as { stepData?: Record<string, unknown> }).stepData,
												name: createdStep.name,
											},
										},
									};
								})
							);

							// Update the phantom edge's switchNodeId to use the real step ID format
							// This ensures handleAddSegment can extract the stepId correctly
							const realSwitchNodeId = `step-${createdStep.id}`;
							setEdges(currentEdges =>
								currentEdges.map(edge => {
									if (
										edge.type !== EDGE_TYPES.PHANTOM_ADD_PATH ||
										(edge.data as { switchNodeId?: string })?.switchNodeId !== switchResult.switchNodeId
									) {
										return edge;
									}
									return {
										...edge,
										data: {
											...edge.data,
											switchNodeId: realSwitchNodeId,
										},
									};
								})
							);
						}
					}
					return;
				}

				// Resolve child template for branch steps.
				// Steps in a branch belong to the case's child template, not the root.
				let nodeTemplate = automationTemplate;
				if (terminus.branchInfo && automationTemplate) {
					const switchNode = nodes.find(n => n.id === terminus.branchInfo!.parentSwitchId);
					const switchStep = switchNode?.data?.automationStep as Api.ISwitchAutomationStep | undefined;
					if (switchStep?.cases) {
						const matchedCase = terminus.branchInfo.caseId
							? switchStep.cases.find(c => c.id === terminus.branchInfo!.caseId)
							: switchStep.cases[terminus.branchInfo.caseIndex];
						if (matchedCase?.automationTemplateId) {
							nodeTemplate = { ...automationTemplate, id: matchedCase.automationTemplateId };
						}
					}
				}

				// Create optimistic node for other step types
				const newNode = createStepNodeByType(stepType, { x: 0, y: 0 }, undefined, nodeTemplate);
				if (!newNode) {
					toast.error(`Unknown step type: ${stepType}`);
					return;
				}

				// Enrich node with parentCaseInfo so the segment badge renders.
				// Only the first child directly below a segment gets the badge —
				// deeper descendants in the same branch do not show it.
				if (terminus.branchInfo && terminus.node.type === NODE_TYPES.SEGMENT) {
					const segData = terminus.node.data as SegmentNodeData;
					newNode.data = {
						...newNode.data,
						parentCaseInfo: {
							caseIndex: segData.segmentIndex,
							caseName: `Segment #${segData.segmentIndex + 1}`,
							isDefault: segData.isDefault,
							caseColor: segData.caseColor,
						},
					};
				}

				takeSnapshot('node_add', `Add ${stepType} step`);

				// Insert node after the terminus with auto-layout.
				// All terminus nodes (segments, step nodes) use a source handle with
				// id='default'. The 'case-X' handles only exist on switch step nodes,
				// which are never termini. Pass undefined to use the default handle.
				const result = insertNodeAfter(nodes, edges, terminus.nodeId, newNode, undefined);

				const updatedEdges = result.edges.map(edge => ({
					...edge,
					animated: animateEdges,
					data: { ...edge.data, activeContacts },
				}));

				setNodes(result.nodes);
				setEdges(updatedEdges);

				// Focus on newly added node
				const addedNode = result.nodes.find(n => n.id === newNode.id);
				if (addedNode) {
					scheduleCenterOnNode(setCenter, addedNode, { duration: 300, zoom: 1 });
				}

				// Persist to backend if handler provided
				if (onAddStepToChain) {
					const createdStep = await onAddStepToChain(terminus, stepType);
					if (createdStep?.id) {
						reconcileOptimisticStepNode({
							optimisticNodeId: newNode.id,
							createdStep,
							automationTemplate: nodeTemplate,
							setNodes,
							setEdges,
						});
					}
				}
			} catch {
				toast.error('Failed to add step. Please try again.');
			}
		},
		[
			onAddStepToChain,
			nodes,
			edges,
			animateEdges,
			activeContacts,
			setNodes,
			setEdges,
			setCenter,
			takeSnapshot,
			automationTemplate,
		]
	);

	return handleAddStepToChain;
}
